import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Zap, 
  Bell, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Wallet,
  History,
  FolderOpen
} from 'lucide-react';
import { useStore } from '../store';
import { useSwipeNavigation } from '../hooks/useSwipeNavigation';
import SkipLink from './SkipLink';

const Layout: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { user, notifications, logout } = useStore();
  const location = useLocation();
  const swipeHandlers = useSwipeNavigation();
  
  const unreadNotifications = notifications.filter(n => !n.read).length;

  const navItems = [
    { path: '/', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
    { path: '/services', label: 'My Services', icon: <Zap size={20} /> },
    { path: '/deals', label: 'Available Deals', icon: <Wallet size={20} /> },
    { path: '/history', label: 'Switch History', icon: <History size={20} /> },
    { path: '/documents', label: 'Documents', icon: <FolderOpen size={20} /> },
    { path: '/notifications', label: 'Notifications', icon: <Bell size={20} />, badge: unreadNotifications },
    { path: '/settings', label: 'Settings', icon: <Settings size={20} /> },
  ];

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" {...swipeHandlers}>
      <SkipLink />
      
      {/* Header */}
      <header className="bg-white shadow-sm" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" aria-label="Home">
                  <Zap className="h-8 w-8 text-indigo-600" aria-hidden="true" />
                  <span className="ml-2 text-xl font-bold text-gray-900">Switcher</span>
                </Link>
              </div>
            </div>
            
            <div className="hidden sm:ml-6 sm:flex sm:items-center">
              {user && (
                <div className="flex items-center">
                  <Link 
                    to="/notifications" 
                    className="relative p-1 rounded-full text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    aria-label={`${unreadNotifications} unread notifications`}
                  >
                    <Bell className="h-6 w-6" aria-hidden="true" />
                    {unreadNotifications > 0 && (
                      <span className="absolute top-0 right-0 block h-4 w-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                        {unreadNotifications}
                      </span>
                    )}
                  </Link>
                  
                  <div className="ml-3 relative">
                    <div className="flex items-center">
                      <div className="ml-3">
                        <div className="text-base font-medium text-gray-800">{user.name}</div>
                        <div className="text-sm font-medium text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </div>
                  
                  <button 
                    onClick={logout}
                    className="ml-4 p-1 rounded-full text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    aria-label="Sign out"
                  >
                    <LogOut className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex items-center sm:hidden">
              <button
                onClick={toggleMobileMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <span className="sr-only">
                  {mobileMenuOpen ? 'Close menu' : 'Open menu'}
                </span>
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Main content */}
        <main id="main-content" className="flex-1 overflow-y-auto bg-gray-50" role="main">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>

        {/* Navigation */}
        <nav
          className="hidden md:flex md:flex-shrink-0"
          role="navigation"
          aria-label="Main navigation"
        >
          <div className="flex flex-col w-64 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="mt-5 flex-1 px-2 space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`${
                      location.pathname === item.path
                        ? 'bg-indigo-50 text-indigo-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                    aria-current={location.pathname === item.path ? 'page' : undefined}
                  >
                    <div className="mr-3" aria-hidden="true">{item.icon}</div>
                    {item.label}
                    {item.badge && item.badge > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-25"
            role="dialog"
            aria-modal="true"
            onClick={toggleMobileMenu}
          >
            <div
              className="fixed inset-y-0 left-0 w-64 bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
                <div className="flex items-center flex-shrink-0 px-4">
                  <Zap className="h-8 w-8 text-indigo-600" aria-hidden="true" />
                  <span className="ml-2 text-xl font-bold text-gray-900">Switcher</span>
                </div>
                <nav className="mt-5 flex-1 px-2 space-y-1">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`${
                        location.pathname === item.path
                          ? 'bg-indigo-50 text-indigo-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                      onClick={toggleMobileMenu}
                      aria-current={location.pathname === item.path ? 'page' : undefined}
                    >
                      <div className="mr-3" aria-hidden="true">{item.icon}</div>
                      {item.label}
                      {item.badge && item.badge > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Layout;